/**
 * IPC Service - Centralized wrapper for Electron IPC communication
 *
 * Resolution order:
 *   1. window.electronAPI (Electron preload bridge — desktop builds)
 *   2. window.require('electron') (legacy fallback)
 *   3. ipcWebShim (web build only, when REACT_APP_WEB=1 or no Electron present)
 */

import { createWebElectronAPI } from './ipcWebShim';

const isWebBuild = process.env.REACT_APP_WEB === '1';

const getAPI = () => {
  // New secure API via preload bridge (Electron desktop)
  if (typeof window !== 'undefined' && window.electronAPI) {
    return window.electronAPI;
  }
  // Web build (no Electron) — use the in-browser shim
  if (isWebBuild || (typeof window !== 'undefined' && !window.require)) {
    const api = createWebElectronAPI();
    if (typeof window !== 'undefined') {
      window.electronAPI = api; // expose so any direct callers also work
    }
    return api;
  }
  // Legacy fallback (Electron with nodeIntegration enabled — not used by current build)
  try {
    const { ipcRenderer } = window.require('electron');
    return {
      invoke: (channel, ...args) => ipcRenderer.invoke(channel, ...args),
      send: (channel, ...args) => ipcRenderer.send(channel, ...args),
      on: (channel, callback) => {
        const handler = (event, ...args) => callback(...args);
        ipcRenderer.on(channel, handler);
        return () => ipcRenderer.removeListener(channel, handler);
      },
      removeListener: (channel, callback) => ipcRenderer.removeListener(channel, callback),
      removeAllListeners: (channel) => ipcRenderer.removeAllListeners(channel)
    };
  } catch (e) {
    console.error('[IPC] No Electron API available');
    return null;
  }
};

const api = getAPI();

export const ipc = {
  invoke: (channel, ...args) => {
    if (!api) return Promise.reject(new Error('IPC not available'));
    return api.invoke(channel, ...args);
  },
  send: (channel, ...args) => {
    if (!api) return;
    api.send(channel, ...args);
  },
  on: (channel, callback) => {
    if (!api) return () => {};
    return api.on(channel, callback);
  },
  removeAllListeners: (channel) => {
    if (!api) return;
    if (api.removeAllListeners) {
      api.removeAllListeners(channel);
    }
  }
};

export default ipc;
